import * as React from 'react';
import { RelatorioFluxoMoneyCell } from '../fluxo/relatorio-fluxo-money-cell';
import { RelatorioFluxoTooltip } from '../fluxo/relatorio-fluxo-tooltip';
import {
  BaSeBreakpointContext,
  BaSeIcon,
  BaSeShapeButton,
  BaSeSmall1,
  BaSeTheme,
  BaSeTag,
} from '@base/react';
import {
  MainBody,
  CardContentWrapper,
  CardLeftPanel,
  CardRightPanel,
  LeftInfoMain,
  RightInfoMain,
  RightInfoCell,
  RightCellContent,
  LeftButtonToggler,
  ExternalShell,
  HeaderLeftInfoMain,
  TruncateText,
} from '../relatorio-mei-styled';
import { SituacaoTags } from '../../../pages/new-relatorios/relatorios-lancamentos';

const DEFAULT_TEXT_COLOR = BaSeTheme.colors.institucionais.cinzaSebrae20;

const RightCell = ({ value, isTag = false, isShow }) => (
  <>
    <RightInfoCell height="32px">
      <RightCellContent>
        {isTag
          ? isShow && <SituacaoTags value={value} />
          : isShow && (
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR}>{value}</BaSeSmall1>
            )}
      </RightCellContent>
    </RightInfoCell>
  </>
);

export const RelatorioContato = ({
  dadosLancamento,
  columnsSelected,
  // eslint-disable-next-line
}) => {
  const { inTablet, inDesktop } = React.useContext(BaSeBreakpointContext);

  const [menuOpen, setMenuOpen] = React.useState(true);
  const [mappedReceitaLancamentos, setMappedReceitaLancamentos] =
    React.useState(null);
  const [mappedDespesaLancamentos, setMappedDespesaLancamentos] =
    React.useState(null);
  const [showOptions, setShowOptions] = React.useState({
    Receita: true,
    Despesa: true,
  });

  const { azulSebrae36 } = BaSeTheme.colors.institucionais;

  const columnsActive = React.useMemo(() => {
    const valor = { id: 6, label: 'Valor' };
    return [...columnsSelected, valor];
  }, [columnsSelected]);

  const sizeColumns = React.useMemo(() => {
    const frame = {
      0: '1fr',
      1: '5fr 1fr',
      2: '2fr 2fr 1fr',
      3: '2fr 2fr 2fr 1fr',
      4: '1fr 1fr 1fr 1fr 1fr',
      5: '1fr 1fr 1fr 1fr 1fr 1fr',
    };
    return frame[columnsSelected.length];
  }, [columnsSelected]);

  const hideInfo = React.useCallback(
    (value) => {
      const currentOption = showOptions[value];
      setShowOptions((prev) => ({ ...prev, [value]: !currentOption }));
    },
    [showOptions],
  );

  const showSingleRow = React.useCallback(() => {
    return (
      <RightInfoMain height="32px" sizeColumns={sizeColumns}>
        {columnsActive?.map((ele, k) => (
          <RightInfoCell blue height="32px" key={k}>
            <RightCellContent></RightCellContent>
          </RightInfoCell>
        ))}
      </RightInfoMain>
    );
  }, [columnsActive, sizeColumns]);

  const formatDate = React.useCallback((data) => {
    if (!data) return '';
    const date = new Date(data);
    return date.toLocaleDateString();
  }, []);

  const showCate = React.useCallback(
    // eslint-disable-next-line
    (lancamentos, showList = false, type = 'Receita') => {
      if (!lancamentos) {
        return;
      }

      if (showList) {
        const showColumns = columnsSelected?.map((item) => item.id);

        return (
          <>
            {lancamentos?.listaDetalhes?.map((item, id) => (
              <React.Fragment key={id}>
                {showOptions[type] && (
                  <>
                    <RightInfoMain height="32px" sizeColumns={sizeColumns}>
                      {showColumns?.includes(1) && (
                        <RightCell
                          value={item.categoria}
                          isShow={showColumns?.includes(1)}
                        />
                      )}
                      {showColumns?.includes(2) && (
                        <RightCell
                          value={item.tipoReceita}
                          isShow={showColumns?.includes(2)}
                        />
                      )}
                      {showColumns?.includes(3) && (
                        <RightCell
                          value={formatDate(item?.dataEfetivacao) ?? ''}
                          isShow={showColumns?.includes(3)}
                        />
                      )}
                      {showColumns?.includes(4) && (
                        <RightCell
                          value={formatDate(item.dataLancamento)}
                          isShow={showColumns?.includes(4)}
                        />
                      )}

                      {showColumns?.includes(5) && (
                        <RightCell
                          isTag={true}
                          value={item.situacao}
                          isShow={showColumns?.includes(5)}
                        />
                      )}

                      <RightInfoCell
                        height="32px"
                        style={{
                          justifyContent: 'end',
                        }}
                      >
                        <RightCellContent
                          justify={'end'}
                          style={{
                            padding: '0 12px',
                          }}
                        >
                          <RelatorioFluxoMoneyCell
                            type={type}
                            value={item?.valor ?? 0}
                          />
                        </RightCellContent>
                      </RightInfoCell>
                    </RightInfoMain>
                  </>
                )}
              </React.Fragment>
            ))}
          </>
        );
      }

      return (
        <>
          {lancamentos &&
            lancamentos?.listaDetalhes?.map(({ descricao }, k) => (
              <React.Fragment key={k}>
                {showOptions[type] && (
                  <>
                    <LeftInfoMain height="32px">
                      <RelatorioFluxoTooltip message={descricao}>
                        <TruncateText>
                          <BaSeSmall1 color={DEFAULT_TEXT_COLOR}>
                            {descricao}
                          </BaSeSmall1>
                        </TruncateText>
                      </RelatorioFluxoTooltip>
                    </LeftInfoMain>
                  </>
                )}
              </React.Fragment>
            ))}
        </>
      );
    },
    [columnsSelected, showOptions, sizeColumns, formatDate],
  );

  const cacheShowReceitas = React.useCallback(
    (onlySize = false) =>
      showCate(mappedReceitaLancamentos, onlySize, 'Receita'),
    [mappedReceitaLancamentos, showCate],
  );

  const cacheShowDespesas = React.useCallback(
    (onlySize = false) =>
      showCate(mappedDespesaLancamentos, onlySize, 'Despesa'),
    [mappedDespesaLancamentos, showCate],
  );

  const listRow = React.useCallback(
    (row, type, border, height, blue, last) => {
      return (
        <React.Fragment>
          {columnsActive?.map((item, i) => (
            <RightInfoCell
              noBorder={border}
              height={height ?? '32px'}
              blue={blue}
              key={i}
              last={last}
              style={{
                justifyContent: item?.label === 'Valor' ? 'end' : 'normal',
              }}
            >
              <RightCellContent
                justify={'end'}
                style={{
                  padding: item?.label === 'Valor' ? '0 12px' : '0 6px',
                }}
              >
                {item?.label === 'Valor' && (
                  <RelatorioFluxoMoneyCell type={type} value={row ?? 0} />
                )}
              </RightCellContent>
            </RightInfoCell>
          ))}
        </React.Fragment>
      );
    },
    [columnsActive],
  );

  const showTitleColumns = React.useMemo(
    () => (
      <>
        {columnsActive?.map((ele) => (
          <RightInfoCell height="80px" key={ele.id}>
            <RightCellContent>
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                {columnsSelected?.includes(ele) || ele?.label === 'Valor'
                  ? ele?.label
                  : ''}
              </BaSeSmall1>
            </RightCellContent>
          </RightInfoCell>
        ))}
      </>
    ),
    [columnsActive, columnsSelected],
  );

  const colorTag = React.useCallback((value) => {
    const colors = {
      Cliente: 'default',
      Fornecedor: 'confirmation',
      'Conta Poupança': 'confirmation',
      'Cartão de crédito': 'attention',
      'Conta Corrente': 'default',
    };
    return colors?.[value] ?? 'default';
  }, []);

  React.useEffect(() => {
    if (!dadosLancamento) {
      return;
    }
    let receitas = {};
    let despesas = {};
    const data = dadosLancamento?.listaLancamentos?.forEach((item) => {
      if (item.tipoLancamento === 'Receita') {
        receitas = item;
      }
      if (item.tipoLancamento === 'Despesa') {
        despesas = item;
      }
    });
    setMappedDespesaLancamentos(despesas);
    setMappedReceitaLancamentos(receitas);

    return data;
  }, [dadosLancamento]);

  return (
    <ExternalShell>
      <MainBody>
        <CardContentWrapper>
          <CardLeftPanel isOpen={menuOpen}>
            {!inTablet && (
              <LeftButtonToggler>
                <BaSeShapeButton
                  onClick={() => setMenuOpen((prev) => !prev)}
                  nameIcon={menuOpen ? 'angle-left' : 'angle-right'}
                  type="secondary"
                  size="small"
                  shape="square"
                />
              </LeftButtonToggler>
            )}
            <HeaderLeftInfoMain height="80px">
              <TruncateText>
                <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                  {dadosLancamento?.nome}
                </BaSeSmall1>
              </TruncateText>
              <TruncateText>
                <BaSeSmall1 color={DEFAULT_TEXT_COLOR}>
                  {dadosLancamento?.nomeBanco ||
                    dadosLancamento?.tipoBandeira ||
                    ''}
                </BaSeSmall1>
              </TruncateText>
              <div
                style={{
                  display: inDesktop ? 'flex' : 'grid',
                  gap: inDesktop ? 10 : 5,
                  alignSelf: 'start',
                }}
              >
                {dadosLancamento?.tipo?.map((item, i) => (
                  <BaSeTag key={i} color={colorTag(item)} label={item} />
                ))}
              </div>
            </HeaderLeftInfoMain>
            <LeftInfoMain
              hasClick
              onClick={() => hideInfo('Receita')}
              height="32px"
              blue
            >
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                Receitas
              </BaSeSmall1>
              <BaSeIcon
                name={
                  showOptions['Receita'] ? 'arrow-head-up' : 'arrow-head-down'
                }
                description="Exibe/Recolhe informações de lançamentos de Receitas"
                size={12}
                color={azulSebrae36}
              />
            </LeftInfoMain>
            {cacheShowReceitas()}
            <LeftInfoMain height="32px">
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                Saldo recebido
              </BaSeSmall1>
            </LeftInfoMain>
            <LeftInfoMain
              hasClick
              onClick={() => hideInfo('Despesa')}
              height="32px"
              blue
            >
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                Despesas
              </BaSeSmall1>
              <BaSeIcon
                name={
                  showOptions['Despesa'] ? 'arrow-head-up' : 'arrow-head-down'
                }
                description="Exibe/Recolhe informações de lançamentos de Despesas"
                size={12}
                color={azulSebrae36}
              />
            </LeftInfoMain>
            {cacheShowDespesas()}
            <LeftInfoMain height="32px">
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                Saldo pago
              </BaSeSmall1>
            </LeftInfoMain>
            <LeftInfoMain height="46px" blue last>
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                Total do período
              </BaSeSmall1>
            </LeftInfoMain>
          </CardLeftPanel>
          <CardRightPanel>
            <RightInfoMain height="80px" sizeColumns={sizeColumns}>
              {showTitleColumns}
            </RightInfoMain>
            {showSingleRow()}
            {showCate(mappedReceitaLancamentos, true)}
            <RightInfoMain height="32px" sizeColumns={sizeColumns}>
              {listRow(mappedReceitaLancamentos?.total, 'auto')}
            </RightInfoMain>
            {showSingleRow()}
            {showCate(mappedDespesaLancamentos, true, 'Despesa')}
            <RightInfoMain height="32px" sizeColumns={sizeColumns}>
              {listRow(mappedDespesaLancamentos?.total, 'auto')}
            </RightInfoMain>
            <RightInfoMain height="36px" sizeColumns={sizeColumns}>
              {listRow(
                dadosLancamento?.totalPeriodo,
                'auto',
                true,
                '36px',
                true,
                true,
              )}
            </RightInfoMain>
          </CardRightPanel>
        </CardContentWrapper>
      </MainBody>
    </ExternalShell>
  );
};
